import {
  CrmFieldService,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import {
  Component,
  computed,
  inject,
  Input,
  input,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RecordChangeType } from '../../../tokens-and-interfaces';
import { TranslationModule } from '../../../i18n/translation-module';
import {
  ObjectTypePrefix,
  VALID_RECORD_CHANGE_TYPES,
} from '../../../constants';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ObserversModule } from '@angular/cdk/observers';
import { CrmActivityOpportunityUpdatedComponent } from '../activities/opportunity-change.component';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { CrmActivityCallComponent } from '../activities/call.component';
import { CrmActivityAssociationPopoverComponent } from '../activities/association-popover.component';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { ActivityAssociationService } from '../activities/activity-association.service';
import { ActivityInterface } from '@vendasta/crm';
import {
  CrmActivityCommunicationComponent,
  CrmActivityEmailComponent,
  CrmActivityLoggedCallComponent,
  CrmActivityLoggedCommunicationComponent,
  CrmActivityLoggedEmailComponent,
  CrmActivityLoggedMeetingComponent,
  CrmActivityLoggedNoteComponent,
  CrmActivityLoggerComponent,
  CrmActivityNoteComponent,
  CrmActivityRecordChangeComponent,
  CrmActivityTaskComponent,
  CrmActivityTemplateComponent,
  CrmActivityTemplateOptionsDirective,
  CrmObjectIdentifier,
} from '../activities';
import {
  CrmActivityEffectiveDatePipe,
  CrmIsTaskOverduePipe,
} from '../../../shared-pipes';
import { CrmTimelineAssociatedObjectPipe } from './timeline-associated-object.pipe';
import {
  ActivitiesTimelineService,
  ActivityExtended,
} from './timeline.service';
import { FileAssociationService } from '../../../shared-services/file-association.service';
import { CrmActivityMeetingComponent } from '../activities/meeting.component';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { encodeFilters } from '@vendasta/galaxy/filter/chips/src/utils';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
  selector: 'crm-timeline-activity',
  templateUrl: './timeline-activity.component.html',
  styleUrls: ['./timeline-activity.component.scss'],
  providers: [ActivityAssociationService, FileAssociationService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    MatIconModule,
    CrmActivityTemplateComponent,
    CrmActivityLoggedNoteComponent,
    ObserversModule,
    CrmActivityRecordChangeComponent,
    CrmActivityOpportunityUpdatedComponent,
    CrmActivityLoggedEmailComponent,
    CrmActivityLoggedCallComponent,
    CrmActivityLoggedMeetingComponent,
    CrmActivityEmailComponent,
    CrmActivityLoggedCommunicationComponent,
    GalaxyFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CrmActivityNoteComponent,
    CrmActivityTaskComponent,
    CrmActivityEffectiveDatePipe,
    CrmIsTaskOverduePipe,
    CrmActivityLoggerComponent,
    CrmActivityTemplateOptionsDirective,
    MatMenuModule,
    MatButtonModule,
    CrmTimelineAssociatedObjectPipe,
    CrmActivityCommunicationComponent,
    CrmActivityCallComponent,
    CrmActivityOpportunityUpdatedComponent,
    CrmActivityAssociationPopoverComponent,
    CrmActivityMeetingComponent,
  ],
})
export class CrmTimelineActivityComponent {
  @Input({ required: true }) activity!: ActivityExtended;
  crmObjectIdentifier = input.required<CrmObjectIdentifier>();
  @Input() isLast = false;
  showCopyLink = input(false);

  private readonly activitiesTimelineService = inject(ActivitiesTimelineService);
  private readonly fieldService = inject(CrmFieldService);
  private readonly clipboard = inject(Clipboard);
  private readonly document = inject(DOCUMENT);
  private readonly snackbarService = inject(SnackbarService);

  uniqueUsers$ = this.activitiesTimelineService.state.select('uniqueUsers');

  editMode = false;
  readonly dateFormat = DateFormat.medium;
  protected readonly hideCrmAssociations = computed(() => {
    const objId = this.crmObjectIdentifier();
    return objId.objectType === 'Opportunity' && !objId.crmObjectId.startsWith(ObjectTypePrefix.Opportunity);
  });

  isActivityValid() {
    const activity = this.activity;
    if (activity === null || activity === undefined) {
      return false;
    }

    switch (activity.crmObjectSubtype) {
      case 'Email':
        return true;
      case 'Note':
        return (
          this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityNoteTitle)?.stringValue ||
          this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityNoteBody)?.stringValue
        );
      case 'Meeting':
        return true;
      case 'Call':
        return true;
      case 'RecordChange':
        return VALID_RECORD_CHANGE_TYPES.includes(
          this.fieldService.getFieldValueFromCrmObject(activity, StandardExternalIds.ActivityRecordChangeType)
            ?.stringValue as RecordChangeType,
        );
      case 'Task':
        return true;
      case 'Communication':
        return true;
      case 'OpportunityChange':
        return true;
      default:
        console.error(`Unexpected activity type: ${activity.crmObjectSubtype}`);
        return false;
    }
  }

  private constructCopyLink(): string {
    const documentUrl = this.document.URL.split('?')[0];

    const glxyFilter: GalaxyFilterInterface = {
      fieldId: SystemFieldIds.ActivityID,
      operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
      values: [
        {
          string: this.activity.crmObjectId,
        },
      ],
    };
    const encodedFilter = encodeFilters([glxyFilter]);
    return `${documentUrl}?filter=${encodedFilter}`;
  }

  copyLinkToActivity(): void {
    this.clipboard.copy(this.constructCopyLink());
    this.snackbarService.openSuccessSnack('COMMON.SNACKBAR_MESSAGE.COPY_SUCCESS');
  }

  updateActivity(activity: ActivityInterface): void {
    const activities = this.activitiesTimelineService.state.get('activities');
    const index = activities.findIndex((a) => a.crmObjectId === this.activity.crmObjectId);
    if (index >= 0) {
      this.activitiesTimelineService.lockActivityEffectiveDate(activities[index]);
      activities[index] = activity;
      this.activitiesTimelineService.state.set({ activities: [...activities] });
      if (this.activity.crmObjectId) {
        this.editMode = false;
      }
    }
  }
}
